import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, RotateCcw, Home, ChevronRight, Sparkles, Volume2, VolumeX, Pause } from 'lucide-react';
import {
  LEVELS, MAX_LEVEL, worldForLevel, computeStars, ENDLESS_UNLOCK_LEVEL, MASTER_UNLOCK_LEVEL,
} from '@/lib/mindOverCityLevels';
import {
  recordRun, playSfx, isSfxMuted, setSfxMuted,
  getMoC, saveMoCLevelResult, saveMoCEndless, subscribe, getMeta,
} from '@/lib/loungeMeta';
import LevelSelect from './lounge/LevelSelect';
import StarBadge from './lounge/StarBadge';
import { useLoungeCountdown, LoungeCountdown } from '@/lib/loungeGame';

/* -------------------- Visual constants -------------------- */
const NAVY = '#0B192C';
const NAVY_2 = '#12253F';
const GOLD = '#C5A059';
const GOLD_S = '#E1C88A';
const ORB_CORE = '#FFE9B0';
const ORB_RING = '#C5A059';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand = (a, b) => a + Math.random() * (b - a);

/* -------------------- Mind Over City -------------------- */
export default function MindOverCity() {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const sectionRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const stateRef = useRef(null);
  const pausedRef = useRef(false);
  const holdRef = useRef(false);
  const navigate = useNavigate();

  // Views: map | ready | playing | over
  const [view, setView] = useState('map');
  const [levelId, setLevelId] = useState(() => {
    const m = getMoC();
    return Math.min(m.lastLevel || 1, m.highestUnlocked || 1);
  });
  const [mode, setMode] = useState('campaign'); // campaign | endless | master
  const [moc, setMoc] = useState(() => getMoC());
  const [muted, setMuted] = useState(() => isSfxMuted());
  useEffect(() => subscribe(() => { setMoc(getMeta().moc); setMuted(isSfxMuted()); }), []);

  // Cancel any pending countdown if we leave the playing view.
  useEffect(() => { if (view !== 'playing') cancelCountdown(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // HUD state (updated every frame)
  const [orbs, setOrbs] = useState(0);
  const [nearMisses, setNearMisses] = useState(0);
  const [paused, setPaused] = useState(false);
  const [runResult, setRunResult] = useState(null); // { stars, orbs, nearMisses, cleared }

  const level = useMemo(() => LEVELS[levelId - 1], [levelId]);
  const world = useMemo(() => worldForLevel(levelId), [levelId]);
  const bestStars = moc.levelStars[levelId] ?? 0;

  const scrollSectionIntoView = () => {
    // Wait two frames so React commits the shorter Ready layout before we scroll.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    });
  };

  /* ---- Canvas resize ---- */
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapperRef.current;
    if (!canvas || !wrap) return;
    const w = Math.min(760, wrap.clientWidth);
    let h = Math.max(320, Math.min(460, Math.round(w * 0.6)));
    // Full-screen embed (native WebView): grow to fill the viewport height.
    if (canvas.closest('[data-embed]')) {
      const top = wrap.getBoundingClientRect().top;
      const avail = Math.round((window.visualViewport?.height || window.innerHeight) - top - 12);
      if (avail > h) h = Math.min(avail, 1000);
    }
    canvas.width = w;
    canvas.height = h;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    return { w, h };
  }, []);

  /* ---- Build fresh play state ---- */
  const buildState = useCallback((W, H, cfg) => ({
    W, H,
    x: W * 0.28,
    y: H * 0.5,
    vy: 0,
    rot: 0,
    size: 22,
    // Physics — meditative glide (identical to previous MoC feel)
    GRAVITY: 0.14,
    HOLD_LIFT: 0.42,
    NUDGE_V: -1.8,
    DRAG: 0.93,
    MAX_VY: 3.4,
    MIN_VY: -3.0,
    // Level-driven pacing (from cfg)
    cfg,
    // Course-completion — level clears when the player passes the FINAL gate.
    courseGates: cfg.courseGates ?? 8,
    spawnedGates: 0,
    finalGateReleased: false,
    gates: [],
    spawnT: 0,
    spawnEvery: cfg.spawnEvery,
    scrollSpeed: cfg.speed,
    orbs: 0,
    nearMisses: 0,
    gameOver: false,
    cleared: false,
    won: false,
    parallax: 0,
    starfield: Array.from({ length: 26 }, () => ({ x: Math.random() * W, y: Math.random() * H * 0.7, s: rand(1, 2.2) })),
    trail: [],
    burst: [],
    trailT: 0,
    // Weather-driven ambient particles (rain / snow / wind / neon motes)
    ambient: buildMoCAmbient(cfg.weather, cfg.wind, W, H),
    // Per-world skyline shape (city blocks / neon towers / mountain peaks)
    skyline: buildMoCSkyline(cfg, W, H),
    // Storm state
    lightning: 0,
    lightningT: rand(3, 8),
    windPhase: 0,
    targetRot: 0,
  }), []);

  /* ---- Gate spawner (Focus Orb gates) ---- */
  const spawnGate = (s) => {
    const cfg = s.cfg;
    // Stop spawning once the whole course has been laid out
    if (s.spawnedGates >= s.courseGates) return;
    const isFinal = (s.spawnedGates + 1) === s.courseGates;
    const baseGap = clamp(s.H * 0.5 - s.orbs * 3.2, 80, s.H * 0.55) * cfg.gapMul;
    const gapY = rand(30, s.H - 50 - baseGap);
    const variant = isFinal
      ? 'standard'                                       // Final gate is always a clean, standard shape
      : cfg.variants[Math.floor(Math.random() * cfg.variants.length)];
    const finalTag = isFinal ? { isFinal: true } : {};

    if (variant === 'narrow') {
      const gh = Math.max(90, baseGap * 0.72);
      s.gates.push({ x: s.W + 20, gapY, gapH: gh, w: 40, passed: false, variant, moveA: 0, moveT: 0, ...finalTag });
    } else if (variant === 'wide-thin') {
      const gh = Math.max(110, baseGap * 0.86);
      s.gates.push({ x: s.W + 20, gapY, gapH: gh, w: 60, passed: false, variant, moveA: 0, moveT: 0, ...finalTag });
    } else if (variant === 'moving') {
      const amp = clamp(18 + s.orbs * 1.5, 20, 60);
      s.gates.push({ x: s.W + 20, gapY, gapH: baseGap, w: 46, passed: false, variant,
                     moveA: amp, moveT: rand(0, Math.PI * 2), baseGapY: gapY, ...finalTag });
    } else if (variant === 'double') {
      const smallGap = Math.max(80, baseGap * 0.42);
      const midBlockH = Math.max(60, baseGap * 0.55);
      const topGapY = clamp(gapY, 24, s.H - 60 - (smallGap + midBlockH + smallGap));
      const bottomGapY = topGapY + smallGap + midBlockH;
      s.gates.push({
        x: s.W + 20,
        gapY: topGapY, gapH: smallGap, w: 46, passed: false, variant,
        midBlockY: topGapY + smallGap, midBlockH,
        secondGapY: bottomGapY, secondGapH: smallGap,
        moveA: 0, moveT: 0, ...finalTag,
      });
    } else {
      // Final gate is rendered slightly wider (a real finish-line feel)
      const finalGap = isFinal ? Math.min(s.H * 0.6, baseGap * 1.12) : baseGap;
      s.gates.push({ x: s.W + 20, gapY, gapH: finalGap, w: isFinal ? 54 : 46, passed: false, variant: 'standard', moveA: 0, moveT: 0, ...finalTag });
    }
    s.spawnedGates += 1;
    if (isFinal) s.finalGateReleased = true;
  };

  const gateRects = (p, dt) => {
    if (p.variant === 'moving') {
      p.moveT += dt * 1.4;
      const off = Math.sin(p.moveT) * p.moveA;
      const gy = p.baseGapY + off;
      return [
        { top: 0, bottom: gy, x: p.x, w: p.w },
        { top: gy + p.gapH, bottom: 9999, x: p.x, w: p.w },
      ];
    }
    if (p.variant === 'double') {
      return [
        { top: 0, bottom: p.gapY, x: p.x, w: p.w },
        { top: p.midBlockY, bottom: p.midBlockY + p.midBlockH, x: p.x, w: p.w },
        { top: p.secondGapY + p.secondGapH, bottom: 9999, x: p.x, w: p.w },
      ];
    }
    return [
      { top: 0, bottom: p.gapY, x: p.x, w: p.w },
      { top: p.gapY + p.gapH, bottom: 9999, x: p.x, w: p.w },
    ];
  };

  const nudge = () => {
    const s = stateRef.current;
    if (!s || s.gameOver) return;
    if (s.vy > s.NUDGE_V) s.vy = s.NUDGE_V;
    s.targetRot = -0.28;
    for (let i = 0; i < 2; i++) {
      s.burst.push({
        x: s.x - 10, y: s.y + 4,
        vx: -rand(30, 60), vy: rand(-6, 12),
        life: 0.5, maxLife: 0.5, size: rand(2, 3), color: GOLD_S,
      });
    }
  };

  const finishRun = (cleared) => {
    const s = stateRef.current;
    if (!s) return;
    s.gameOver = true;
    s.cleared = cleared;
    const runOrbs = s.orbs;
    const runNear = s.nearMisses;
    let stars = 0;
    if (mode === 'campaign') {
      // Course completion = ⭐; near-misses drop stars.
      stars = computeStars(level, { completed: cleared, nearMisses: runNear });
    }
    // Death animation particles
    if (!cleared) {
      for (let i = 0; i < 18; i++) {
        const a = Math.random() * Math.PI * 2;
        const v = rand(60, 200);
        s.burst.push({
          x: s.x, y: s.y,
          vx: Math.cos(a) * v, vy: Math.sin(a) * v,
          life: 0.9, maxLife: 0.9, size: rand(2.5, 5),
          color: Math.random() < 0.5 ? GOLD : GOLD_S,
        });
      }
    }
    if (mode === 'campaign') {
      recordRun('flyer', { level: levelId, score: runOrbs, orbs: runOrbs, nearMisses: runNear,
                            distance: 0, tokens: runOrbs, perfect: stars === 3, cleared, stars });
      saveMoCLevelResult(levelId, { stars, orbs: runOrbs, nearMisses: runNear, cleared });
    } else {
      recordRun('flyer', { level: levelId, score: runOrbs, orbs: runOrbs, nearMisses: runNear,
                            distance: 0, tokens: runOrbs, perfect: false, cleared: false, mode });
      saveMoCEndless(runOrbs, mode);
    }
    playSfx(cleared ? 'levelClear' : 'collision');
    if (cleared && stars === 3) playSfx('star', { volume: 0.35 });
    setRunResult({ stars, orbs: runOrbs, nearMisses: runNear, cleared });
    setView('over');
  };

  /* ---- Main loop ---- */
  const loop = useCallback((ts) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const s = stateRef.current;
    if (!s) return;
    const ctx = canvas.getContext('2d');
    const dt = lastTsRef.current ? Math.min(0.05, (ts - lastTsRef.current) / 1000) : 0;
    lastTsRef.current = ts;

    if (!pausedRef.current && !s.gameOver) {
      s.parallax = (s.parallax + dt * 22) % s.W;
      s.windPhase += dt;

      // Course-completion win — cleared when the player passes the FINAL gate
      if (mode === 'campaign' && !s.won) {
        // The finishing frame is triggered from the pass-detection loop below via `s.won`.
        // Nothing to do here anymore.
      }

      // Physics
      s.vy += s.GRAVITY;
      if (holdRef.current) s.vy -= s.HOLD_LIFT;
      // Wind (world 2/3): gentle vertical drift
      if (s.cfg.wind > 0) s.vy += Math.sin(s.windPhase * 0.9) * (s.cfg.wind * 0.006);
      s.vy *= s.DRAG;
      s.vy = clamp(s.vy, s.MIN_VY, s.MAX_VY);
      s.y += s.vy * (dt * 60);

      // Rotation smoothing
      const desired = s.vy > 0
        ? clamp(s.vy * 0.06, 0, 0.28)
        : clamp(s.vy * 0.08, -0.28, 0);
      s.targetRot = s.targetRot * 0.7 + desired * 0.3;
      s.rot += (s.targetRot - s.rot) * 0.06;

      // Trail
      s.trailT += dt;
      if (s.trailT > 0.1) {
        s.trailT = 0;
        s.trail.push({ x: s.x - 4, y: s.y + rand(-2, 2), size: 3, life: 0.6, maxLife: 0.6 });
      }
      for (const p of s.trail) { p.life -= dt; p.x -= dt * 40; p.size -= dt * 3; }
      s.trail = s.trail.filter(p => p.life > 0);

      for (const p of s.burst) {
        p.life -= dt;
        p.vy += 260 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.98; p.vy *= 0.99;
      }
      s.burst = s.burst.filter(p => p.life > 0);

      // Ambient particles motion (rain streaks, snow drift, wind streaks)
      for (const a of s.ambient) {
        if (a.kind === 'rain') {
          a.y += a.v * dt; a.x -= (30 + s.cfg.wind * 4) * dt;
        } else if (a.kind === 'snow') {
          a.y += a.v * dt; a.x += Math.sin((a.y + a.x) * 0.02) * 20 * dt;
        } else if (a.kind === 'wind') {
          a.x -= a.v * dt; a.y += Math.sin(s.windPhase + a.phase) * 6 * dt;
        } else if (a.kind === 'neon') {
          a.y += Math.sin((a.x + s.windPhase * 20) * 0.03) * 8 * dt; a.x -= 20 * dt;
        }
        if (a.y > s.H + 8) { a.y = -6; a.x = Math.random() * s.W; }
        if (a.x < -8) { a.x = s.W + 8; a.y = Math.random() * s.H; }
      }

      // Storm lightning tick
      if (s.cfg.weather === 'storm') {
        s.lightningT -= dt;
        if (s.lightningT <= 0) { s.lightning = 1; s.lightningT = rand(4, 9); }
        if (s.lightning > 0) s.lightning = Math.max(0, s.lightning - dt * 3);
      }

      // Gate spawning
      s.spawnT += dt;
      if (s.spawnT >= s.spawnEvery) {
        s.spawnT = 0;
        spawnGate(s);
      }
      for (const p of s.gates) p.x -= s.scrollSpeed * dt;
      s.gates = s.gates.filter(p => p.x + p.w > -8);

      // Difficulty ramp within a run
      s.scrollSpeed = s.cfg.speed + Math.min(s.orbs * 3.2, s.cfg.speedRampMax);
      s.spawnEvery = Math.max(s.cfg.spawnMin, s.cfg.spawnEvery - s.orbs * 0.03);

      // Collision + orb collect
      const px = s.x, py = s.y, r = s.size / 2;
      if (py - r < 4 || py + r > s.H - 20) {
        // Any crash = level fails (no more "partial credit" — survival is the only way)
        finishRun(false);
      }
      for (const p of s.gates) {
        // Orb collection = passing through the centre of the gate
        if (!p.passed && p.x + p.w < px - r) {
          p.passed = true;
          s.orbs += 1;
          setOrbs(s.orbs);
          // Sparkle burst at gate centre
          const cy = gateCentre(p);
          const burstCount = p.isFinal ? 22 : 6;
          const burstColor = p.isFinal ? '#FFF3C4' : ORB_CORE;
          for (let i = 0; i < burstCount; i++) {
            const ang = Math.random() * Math.PI * 2;
            s.burst.push({
              x: p.x + p.w / 2, y: cy,
              vx: Math.cos(ang) * rand(30, p.isFinal ? 160 : 80),
              vy: Math.sin(ang) * rand(30, p.isFinal ? 160 : 80),
              life: p.isFinal ? 0.9 : 0.55, maxLife: p.isFinal ? 0.9 : 0.55,
              size: rand(2, p.isFinal ? 5 : 4), color: burstColor,
            });
          }
          playSfx('orb', { volume: 0.35 });
          // Final-gate pass = course completed
          if (p.isFinal && mode === 'campaign' && !s.won) {
            s.won = true;
            playSfx('levelClear');
            // Slight delay so the burst is visible before the over-overlay
            setTimeout(() => finishRun(true), 550);
          }
        }
        // Near-miss detection: passed by less than 12px on either side of the gap
        if (!p.nearRegistered) {
          const segs = gateRects(p, dt);
          if (px + r > p.x && px - r < p.x + p.w) {
            for (const seg of segs) {
              const dTop = Math.abs(py - r - seg.bottom);
              const dBottom = Math.abs(py + r - seg.top);
              if ((seg.top < 20 && dBottom < 12) || (seg.bottom > s.H && dTop < 12)) {
                s.nearMisses += 1;
                setNearMisses(s.nearMisses);
                p.nearRegistered = true;
                break;
              }
            }
          }
        }
        // Hit test
        const segs = gateRects(p, dt);
        const hitX = px + r > p.x && px - r < p.x + p.w;
        if (hitX) {
          for (const seg of segs) {
            if (py + r > seg.top && py - r < seg.bottom) {
              finishRun(false);
              break;
            }
          }
          if (s.gameOver) break;
        }
      }
    }

    /* ---- Render ---- */
    const { W, H } = s;
    // Backdrop varies with weather
    const bg = weatherGradient(ctx, W, H, s.cfg.weather);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Starfield — night-ish weathers only
    if (['clear', 'dusk', 'snow', 'aurora'].includes(s.cfg.weather)) {
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      for (const st of s.starfield) {
        const sx = (st.x + s.parallax * 0.15) % W;
        ctx.fillRect(sx, st.y, st.s, st.s);
      }
    }

    // Per-world skyline (city / neon towers / mountain peaks)
    drawMoCSkyline(ctx, s.skyline, s.parallax, H);

    // Ambient overlay (rain / snow / wind streaks / neon motes)
    drawMoCAmbient(ctx, s.ambient, s.cfg.weather);

    // Storm lightning flash
    if (s.cfg.weather === 'storm' && s.lightning > 0) {
      ctx.fillStyle = `rgba(230,240,255,${s.lightning * 0.35})`;
      ctx.fillRect(0, 0, W, H);
    }

    // Ground line
    ctx.fillStyle = NAVY_2;
    ctx.fillRect(0, H - 20, W, 20);
    ctx.strokeStyle = GOLD; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(0, H - 20); ctx.lineTo(W, H - 20); ctx.stroke();

    // Gates + orb marker at the gate centre
    for (const p of s.gates) {
      const segs = gateRects(p, 0);
      let bodyColor = GOLD;
      let capColor = GOLD_S;
      if (p.variant === 'narrow') { bodyColor = '#B8894A'; capColor = '#D9B575'; }
      else if (p.variant === 'moving') { bodyColor = '#D0AC66'; capColor = '#EBD196'; }
      else if (p.variant === 'double') { bodyColor = '#A87B3E'; capColor = '#CFA968'; }
      // Final gate — always renders with a bright, unmistakable finish-line palette
      if (p.isFinal) { bodyColor = '#EBD196'; capColor = '#FFF3C4'; }
      ctx.fillStyle = bodyColor;
      for (const seg of segs) {
        const top = Math.max(0, seg.top);
        const bottom = Math.min(H - 20, seg.bottom);
        if (bottom > top) {
          ctx.fillRect(seg.x, top, seg.w, bottom - top);
          // Final gate: draw a black-and-white checkered strip up the outer edge
          if (p.isFinal) {
            for (let cy0 = top; cy0 < bottom; cy0 += 10) {
              const alt = (Math.floor((cy0 - top) / 10) % 2 === 0);
              ctx.fillStyle = alt ? '#0A1122' : '#FFFFFF';
              ctx.fillRect(seg.x, cy0, 6, Math.min(10, bottom - cy0));
              ctx.fillRect(seg.x + seg.w - 6, cy0, 6, Math.min(10, bottom - cy0));
            }
            ctx.fillStyle = bodyColor;
          }
        }
      }
      ctx.fillStyle = capColor;
      if (p.variant === 'moving') {
        const off = Math.sin(p.moveT) * p.moveA;
        const gy = p.baseGapY + off;
        ctx.fillRect(p.x - 3, gy - 8, p.w + 6, 8);
        ctx.fillRect(p.x - 3, gy + p.gapH, p.w + 6, 8);
      } else if (p.variant === 'double') {
        ctx.fillRect(p.x - 3, p.gapY - 8, p.w + 6, 8);
        ctx.fillRect(p.x - 3, p.midBlockY, p.w + 6, 6);
        ctx.fillRect(p.x - 3, p.midBlockY + p.midBlockH - 6, p.w + 6, 6);
        ctx.fillRect(p.x - 3, p.secondGapY + p.secondGapH, p.w + 6, 8);
      } else {
        ctx.fillRect(p.x - 3, p.gapY - 8, p.w + 6, 8);
        ctx.fillRect(p.x - 3, p.gapY + p.gapH, p.w + 6, 8);
      }
      // Final gate: a "FINISH" banner floating above the gap
      if (p.isFinal && !p.passed) {
        const bx = p.x - 8;
        const by = Math.max(4, p.gapY - 30);
        ctx.fillStyle = 'rgba(255,243,196,0.95)';
        ctx.fillRect(bx, by, p.w + 16, 16);
        ctx.fillStyle = '#0B192C';
        ctx.font = '700 10px system-ui';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('FINISH', bx + (p.w + 16) / 2, by + 8);
      }
      // Focus Orb marker floating in the gap
      if (!p.passed) {
        const cy = gateCentre(p);
        const pulse = 0.85 + Math.sin(s.windPhase * 4 + p.x * 0.02) * 0.15;
        drawOrb(ctx, p.x + p.w / 2, cy, (p.isFinal ? 14 : 9) * pulse);
      }
    }

    // Player trail
    for (const p of s.trail) {
      const a = p.life / p.maxLife;
      ctx.globalAlpha = a * 0.6;
      ctx.fillStyle = GOLD;
      ctx.fillRect(p.x, p.y, Math.max(1, p.size), Math.max(1, p.size));
    }
    ctx.globalAlpha = 1;

    for (const p of s.burst) {
      const a = p.life / p.maxLife;
      ctx.globalAlpha = clamp(a, 0, 1);
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x, p.y, p.size, p.size);
    }
    ctx.globalAlpha = 1;

    // Player
    if (!s.gameOver) {
      ctx.save();
      ctx.translate(s.x, s.y);
      ctx.rotate(s.rot);
      ctx.shadowColor = holdRef.current ? GOLD_S : GOLD;
      ctx.shadowBlur = holdRef.current ? 18 : 10;
      ctx.fillStyle = GOLD;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-s.size / 2, -s.size / 2, s.size, s.size, 6);
        ctx.fill();
      } else {
        ctx.fillRect(-s.size / 2, -s.size / 2, s.size, s.size);
      }
      ctx.shadowBlur = 0;
      ctx.font = '700 12px system-ui';
      ctx.fillStyle = NAVY;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('B+', 0, 0);
      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(loop);
    // Stable RAF loop — reads all mutable state via refs. Deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, mode, levelId]);

  /* ---- Start / restart ---- */
  const { countdown, beginCountdown, cancelCountdown } = useLoungeCountdown();
  const startPlaying = () => {
    const dim = fit();
    if (!dim) return;
    // Endless / Master use a synthetic cfg with faster ramps
    const cfg = mode === 'campaign' ? level
      : mode === 'endless' ? synthesizeEndlessCfg(false)
      : synthesizeEndlessCfg(true);
    stateRef.current = buildState(dim.w, dim.h, cfg);
    setOrbs(0);
    setNearMisses(0);
    setPaused(false);
    setRunResult(null);
    // Freeze physics during 3-2-1-GO; render still runs.
    pausedRef.current = true;
    setView('playing');
    lastTsRef.current = 0;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(loop);
    playSfx('button', { volume: 0.3 });
    beginCountdown(() => {
      pausedRef.current = false;
      lastTsRef.current = 0;
    });
  };

  const togglePause = () => {
    if (view !== 'playing') return;
    if (countdown) return; // Ignore pause during countdown
    const next = !pausedRef.current;
    pausedRef.current = next;
    setPaused(next);
    playSfx('button', { volume: 0.25 });
  };

  const advanceLevel = () => {
    const nx = Math.min(MAX_LEVEL, levelId + 1);
    setLevelId(nx);
    setTimeout(startPlaying, 60);
  };

  /* ---- View lifecycle ---- */
  useEffect(() => {
    fit();
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = weatherGradient(ctx, canvas.width, canvas.height, level.weather);
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const onResize = () => fit();
    window.addEventListener('resize', onResize);
    const onVis = () => { pausedRef.current = document.hidden; };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fit, level.weather]);

  useEffect(() => {
    if (view !== 'playing') return;
    const onDown = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
        e.preventDefault();
        if (!holdRef.current) nudge();
        holdRef.current = true;
      }
    };
    const onUp = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') holdRef.current = false;
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, [view]);

  const onPointerDown = (e) => {
    // Ignore taps on interactive overlay controls (Play again / Next level / Level select)
    if (e.target && e.target.closest && e.target.closest('button')) return;
    e.preventDefault();
    if (view === 'ready') { startPlaying(); holdRef.current = true; return; }
    if (view !== 'playing') return;
    if (!holdRef.current) nudge();
    holdRef.current = true;
  };
  const onPointerUp = () => { holdRef.current = false; };

  /* ---- Renders ---- */
  if (view === 'map') {
    return (
      <section ref={sectionRef} className="bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden scroll-mt-4" data-testid="mind-over-city">
        <div className="relative w-full aspect-[16/9] overflow-hidden"
             style={{ background: 'linear-gradient(135deg, #0B192C 0%, #12253F 100%)' }}>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-4 text-center">
            <p className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold">Mind Over City</p>
            <p className="font-heading text-2xl lg:text-3xl text-white/95 mt-2">Stay focused. Collect Focus Orbs. Keep flowing.</p>
          </div>
          <img
            src="/images/lounge/mind-over-city.png"
            alt="Mind Over City"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
        <div className="p-4 lg:p-6 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-navy/40 font-semibold">Campaign progress</p>
              <p className="font-heading text-lg text-navy mt-0.5">
                {moc.highestUnlocked - 1} of {MAX_LEVEL} levels cleared · <span className="text-gold">
                  {Object.values(moc.levelStars).reduce((a, b) => a + b, 0)}
                </span> / {MAX_LEVEL * 3} stars
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setSfxMuted(!muted)} data-testid="moc-mute"
                      className="rounded-full w-9 h-9 flex items-center justify-center border border-navy/10 text-navy/70 hover:text-navy hover:border-navy/25 transition-smooth"
                      aria-label={muted ? 'Unmute' : 'Mute'}>
                {muted ? <VolumeX size={14}/> : <Volume2 size={14}/>}
              </button>
              <button type="button" onClick={() => { setLevelId(moc.lastLevel || 1); setMode('campaign'); setView('ready'); }}
                      data-testid="moc-resume"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white font-semibold text-sm hover:brightness-110 transition-smooth">
                Resume Level {Math.min(moc.lastLevel || 1, moc.highestUnlocked || 1)} <ChevronRight size={14}/>
              </button>
            </div>
          </div>

          <LevelSelect
            moc={moc}
            onPick={(id) => { setLevelId(id); setMode('campaign'); setView('ready'); playSfx('button', { volume: 0.3 }); scrollSectionIntoView(); }}
            onEndless={() => { setMode('endless'); setView('ready'); playSfx('unlock', { volume: 0.3 }); scrollSectionIntoView(); }}
            onMaster={() => { setMode('master'); setView('ready'); playSfx('unlock', { volume: 0.3 }); scrollSectionIntoView(); }}
          />

          <p className="text-center text-xs text-navy/40 leading-relaxed">
            Clear a level to unlock the next. Replay any cleared level to raise your stars.
          </p>
        </div>
      </section>
    );
  }

  const currentBest = mode === 'endless' ? moc.endlessBest : mode === 'master' ? moc.masterBest : (moc.levelBest[levelId]?.orbs ?? 0);
  const goal = mode === 'campaign' ? level.starThresholds.three : Infinity;

  return (
    <section ref={sectionRef} className="bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden scroll-mt-4" data-testid="mind-over-city">
      {/* HUD strip above canvas */}
      <div className="px-4 lg:px-5 pt-4 pb-2 flex items-center justify-between gap-3">
        <button type="button" onClick={() => setView('map')} data-testid="moc-back-map"
                className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1.5 transition-smooth">
          ← Level select
        </button>
        <div className="text-center">
          <p className="text-[9px] uppercase tracking-[0.28em] text-navy/40 font-semibold">
            {mode === 'campaign' ? `World ${world.id} · ${world.name}` : mode === 'endless' ? 'Endless Flow' : 'Master Flow'}
          </p>
          <p className="font-heading text-base text-navy mt-0.5">
            {mode === 'campaign' ? `Level ${levelId} — ${level.name}` : (mode === 'endless' ? 'Play as long as you can' : 'The hardest run')}
          </p>
        </div>
        <button type="button" onClick={() => setView('map')} data-testid="moc-home"
                className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1.5 transition-smooth"
                aria-label="Back to level select">
          <Home size={13}/>
        </button>
      </div>

      <div className="px-4 lg:px-5 pb-4">
        <div
          ref={wrapperRef}
          className="relative rounded-2xl overflow-hidden border border-navy/10 select-none"
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <canvas ref={canvasRef} className={`block w-full touch-none ${view === 'ready' ? 'hidden' : ''}`} data-testid="moc-canvas"/>

          {/* 3-2-1-GO countdown (shared across all Lounge games) */}
          <LoungeCountdown n={countdown} testid="moc-countdown" color="#F5DAA6" />

          {/* HUD overlay */}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 pointer-events-none z-10">
            <div className="flex items-center gap-2">
              <div className="rounded-xl px-3 py-1.5 bg-black/40 backdrop-blur-md border border-white/10">
                <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Focus Orbs</p>
                <p className="font-heading text-2xl text-white leading-none mt-0.5 tabular-nums" data-testid="moc-orbs">{orbs}</p>
              </div>
              {mode === 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/40 backdrop-blur-md border border-gold/25">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Course</p>
                  <p className="font-heading text-2xl text-gold leading-none mt-0.5 tabular-nums" data-testid="moc-course">
                    {orbs}<span className="text-white/50 text-base">/{level.courseGates}</span>
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {mode === 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/40 backdrop-blur-md border border-gold/25 text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Best</p>
                  <div className="mt-0.5"><StarBadge count={bestStars} size={11} testid="moc-hud-best-stars"/></div>
                </div>
              )}
              {mode !== 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/40 backdrop-blur-md border border-gold/25 text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Best</p>
                  <p className="font-heading text-2xl text-gold leading-none mt-0.5 tabular-nums" data-testid="moc-hud-best">{currentBest}</p>
                </div>
              )}
            </div>
          </div>

          {/* Pause button (only visible while playing) */}
          {view === 'playing' && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-auto z-10">
              <button onClick={togglePause} data-testid="moc-pause"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-navy bg-gold hover:brightness-95 transition-smooth shadow">
                {paused ? <><Play size={12}/> Resume</> : <><Pause size={12}/> Pause</>}
              </button>
            </div>
          )}

          {/* Paused overlay */}
          {view === 'playing' && paused && (
            <div className="absolute inset-0 backdrop-blur-md flex flex-col items-center justify-center text-white z-20 px-5 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.88) 0%, rgba(18,37,63,0.92) 100%)' }}
                 data-testid="moc-paused">
              <p className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold">Paused</p>
              <p className="font-heading text-2xl mt-2">Take a breath</p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button onClick={togglePause} data-testid="moc-resume"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gold text-navy font-semibold hover:brightness-95 text-sm">
                  <Play size={14}/> Resume
                </button>
                <button onClick={() => setView('map')} data-testid="moc-pause-map"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/8 border border-white/15 text-white hover:bg-white/15 text-sm">
                  Level select
                </button>
              </div>
            </div>
          )}

          {view === 'ready' && (
            <div className="relative w-full backdrop-blur-md flex flex-col items-center text-white z-20 px-5 py-8 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.9) 0%, rgba(18,37,63,0.94) 100%)' }}
                 data-testid="moc-ready">
              <div className="my-auto w-full flex flex-col items-center py-6">
              <p className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold">
                {mode === 'campaign' ? `Level ${levelId} · ${world.name}` : mode === 'endless' ? 'Endless Flow' : 'Master Flow'}
              </p>
              <p className="font-heading text-3xl lg:text-4xl mt-2 max-w-lg leading-tight">
                {mode === 'campaign' ? level.name : (mode === 'endless' ? 'How long can you flow?' : 'The hardest run of your life')}
              </p>
              {mode === 'campaign' && (
                <p className="mt-3 text-sm text-white/80 max-w-md italic">{level.intro}</p>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs text-white/75">
                {mode === 'campaign' && (
                  <>
                    <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10">Course · <span className="text-gold tabular-nums">{level.courseGates}</span> gates</span>
                    <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10 inline-flex items-center gap-1">
                      2<Sparkles size={10} className="text-gold"/> ≤ 1 close-call
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10 inline-flex items-center gap-1">
                      3<Sparkles size={10} className="text-gold"/> 0 close-calls
                    </span>
                  </>
                )}
              </div>
              <div className="mt-4 text-xs text-white/60 leading-relaxed max-w-sm">
                <p><span className="text-gold font-semibold">Hold</span> pointer or space to rise · <span className="text-gold font-semibold">release</span> to drift</p>
              </div>
              <button
                onClick={startPlaying}
                data-testid="moc-start"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-navy font-semibold hover:brightness-95 transition-smooth"
              >
                <Play size={15}/> Start
              </button>
              </div>
            </div>
          )}

          {view === 'over' && runResult && (
            <div className="absolute inset-0 backdrop-blur-md flex flex-col items-center justify-center text-white z-20 px-5 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.92) 0%, rgba(18,37,63,0.95) 100%)' }}
                 data-testid="moc-over">
              <p className={`text-[10px] uppercase tracking-[0.32em] font-semibold ${runResult.cleared ? 'text-gold' : 'text-red-300'}`}>
                {runResult.cleared ? (mode === 'campaign' ? `Level ${levelId} clear · course complete` : 'Nice flow') : (mode === 'campaign' ? 'You crashed' : 'End of run')}
              </p>
              {!runResult.cleared && mode === 'campaign' && (
                <p className="text-xs text-white/60 mt-2 max-w-xs">Complete every gate in the course to earn <span className="text-gold font-semibold">⭐</span>. Fewer close-calls = more stars.</p>
              )}
              {mode === 'campaign' && runResult.cleared && (
                <div className="mt-3"><StarBadge count={runResult.stars} size={30} testid="moc-over-stars"/></div>
              )}
              <p className="font-heading text-3xl lg:text-4xl mt-3">
                {runResult.orbs} <span className="text-white/50 text-base">Focus Orbs</span>
              </p>
              <p className="text-xs text-white/60 mt-1">
                {runResult.nearMisses > 0 ? `${runResult.nearMisses} close-call${runResult.nearMisses > 1 ? 's' : ''}` : 'Zero close-calls'}
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button onClick={startPlaying} data-testid="moc-retry"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gold text-navy font-semibold hover:brightness-95 text-sm">
                  <RotateCcw size={14}/> Play again
                </button>
                {mode === 'campaign' && runResult.cleared && levelId < MAX_LEVEL && (
                  <button onClick={advanceLevel} data-testid="moc-next-level"
                          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 text-white font-semibold hover:bg-white/15 text-sm">
                    Next level <ChevronRight size={14}/>
                  </button>
                )}
                <button onClick={() => setView('map')} data-testid="moc-over-map"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 hover:text-white text-sm">
                  Level select
                </button>
              </div>
            </div>
          )}
        </div>
        <p className="text-[11px] text-navy/40 mt-2 text-center hidden lg:block">
          Hold pointer / space to rise · release to drift · collect Focus Orbs · aim for 3 stars.
        </p>
      </div>
    </section>
  );
}

/* -------- helpers -------- */
function formatTime(secs) {
  const s = Math.max(0, Math.ceil(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
}
function gateCentre(p) {
  if (p.variant === 'moving') return p.baseGapY + Math.sin(p.moveT) * p.moveA + p.gapH / 2;
  if (p.variant === 'double') return p.gapY + p.gapH / 2;
  return p.gapY + p.gapH / 2;
}

function drawOrb(ctx, x, y, r) {
  const grad = ctx.createRadialGradient(x, y, 0, x, y, r * 1.6);
  grad.addColorStop(0, ORB_CORE);
  grad.addColorStop(0.6, ORB_RING);
  grad.addColorStop(1, 'rgba(197,160,89,0)');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, r * 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = ORB_CORE;
  ctx.beginPath();
  ctx.arc(x, y, r * 0.55, 0, Math.PI * 2);
  ctx.fill();
}

function weatherGradient(ctx, W, H, weather) {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  switch (weather) {
    case 'dusk':
      g.addColorStop(0, '#1A2A45'); g.addColorStop(1, '#0B192C'); break;
    case 'storm':
      g.addColorStop(0, '#0A1220'); g.addColorStop(1, '#050A14'); break;
    case 'rain':
      g.addColorStop(0, '#12253F'); g.addColorStop(1, '#0B192C'); break;
    case 'neon':
      g.addColorStop(0, '#0F1A2E'); g.addColorStop(1, '#1A2B4A'); break;
    case 'aurora':
      g.addColorStop(0, '#0B1E29'); g.addColorStop(0.5, '#134058'); g.addColorStop(1, '#0B192C'); break;
    case 'snow':
      g.addColorStop(0, '#1B2942'); g.addColorStop(1, '#0B1E29'); break;
    default: // clear
      g.addColorStop(0, NAVY); g.addColorStop(1, NAVY_2);
  }
  return g;
}

/**
 * Endless / Master cfg — synthesises a fast, gently-ramping level config
 * that keeps getting harder the longer you survive.
 */
function synthesizeEndlessCfg(master) {
  return {
    courseGates: 999999,          // Endless/Master never end via course completion
    gapMul: master ? 0.72 : 0.86,
    spawnEvery: master ? 1.6 : 1.9,
    spawnMin: master ? 0.85 : 1.0,
    speed: master ? 102 : 92,
    speedRampMax: master ? 120 : 90,
    variants: master
      ? ['standard', 'narrow', 'moving', 'double', 'wide-thin']
      : ['standard', 'narrow', 'wide-thin', 'moving'],
    wind: master ? 18 : 8,
    weather: master ? 'aurora' : 'neon',
    starThresholds: { one: 0, two: 1, three: 0 },
  };
}

/* ---------- Per-world skyline + ambient (visual variety across levels) ---------- */

function buildMoCSkyline(cfg, W, H) {
  const baseY = H - 24;
  const theme = cfg.skylineTheme || 'grid';
  if (theme === 'grid') {
    return { kind: 'grid', baseY, items: Array.from({ length: 16 }, (_, i) => ({
      x: i * 66 + rand(-6, 6), w: 44 + Math.floor(rand(-6, 12)), h: 40 + ((i * 17) % 30) + Math.floor(rand(-4, 10)),
    })) };
  }
  if (theme === 'bridge') {
    return { kind: 'bridge', baseY,
      // A series of bridge arches with cables + a few dock lights at ground level
      arches: Array.from({ length: 5 }, (_, i) => ({ x: i * 180 + rand(-20, 20), w: 150, h: 70 + Math.floor(rand(-8, 12)) })),
      docks: Array.from({ length: 20 }, (_, i) => ({ x: i * 48 + rand(-6, 6), h: 12 + Math.floor(rand(0, 8)) })),
    };
  }
  if (theme === 'neon') {
    return { kind: 'neon', baseY, items: Array.from({ length: 12 }, (_, i) => ({
      x: i * 78 + rand(-8, 8), w: 34 + Math.floor(rand(-4, 14)), h: 60 + ((i * 23) % 55) + Math.floor(rand(-6, 12)),
      hue: [280, 320, 200, 180][i % 4],
    })) };
  }
  if (theme === 'overpass') {
    return { kind: 'overpass', baseY,
      // Highway support columns + hanging exit signs above
      pillars: Array.from({ length: 6 }, (_, i) => ({ x: i * 140 + rand(-10, 10), w: 22, h: 90 + Math.floor(rand(-8, 12)) })),
      signs:   Array.from({ length: 4 }, (_, i) => ({ x: i * 200 + rand(-10, 10), y: rand(40, 90), w: 60, h: 22 })),
    };
  }
  if (theme === 'rooftops') {
    return { kind: 'rooftops', baseY, items: Array.from({ length: 12 }, (_, i) => ({
      x: i * 88 + rand(-8, 8), w: 66 + Math.floor(rand(-6, 14)),
      h: 55 + ((i * 29) % 40) + Math.floor(rand(-4, 12)),
      antenna: (i % 3 === 0),
      tank: (i % 4 === 2),
    })) };
  }
  // ridge (Mastery)
  return { kind: 'ridge', baseY, items: Array.from({ length: 8 }, (_, i) => ({
    x: i * 110 + rand(-10, 10), w: 130 + Math.floor(rand(-12, 30)),
    h: 80 + ((i * 41) % 60) + Math.floor(rand(-8, 20)),
  })) };
}

function drawMoCSkyline(ctx, sky, parallax, H) {
  if (!sky) return;
  const baseY = sky.baseY;
  const W = ctx.canvas.width;
  const wrap = (x, span, off) => (((x - parallax * off) % (W + span)) + (W + span)) % (W + span) - span / 2;

  if (sky.kind === 'grid') {
    ctx.fillStyle = '#08111f';
    for (const b of sky.items) {
      const bx = wrap(b.x, 68, 0.4);
      ctx.fillRect(bx, baseY - b.h, b.w, b.h);
    }
    ctx.fillStyle = 'rgba(197,160,89,0.32)';
    for (const b of sky.items) {
      const bx = wrap(b.x, 68, 0.4);
      for (let wy = 8; wy < b.h - 4; wy += 8) {
        for (let wx = 4; wx < b.w - 4; wx += 8) {
          if (((bx + wx + wy) | 0) % 3 === 0) ctx.fillRect(bx + wx, baseY - b.h + wy, 2, 2);
        }
      }
    }
    return;
  }

  if (sky.kind === 'bridge') {
    // Water line
    ctx.fillStyle = 'rgba(120,150,180,0.15)';
    ctx.fillRect(0, baseY - 6, W, 6);
    // Bridge arches
    ctx.strokeStyle = '#0A1626';
    ctx.lineWidth = 6;
    ctx.fillStyle = '#0A1626';
    for (const a of sky.arches) {
      const ax = wrap(a.x, 180, 0.5);
      ctx.beginPath();
      ctx.moveTo(ax, baseY);
      ctx.quadraticCurveTo(ax + a.w / 2, baseY - a.h, ax + a.w, baseY);
      ctx.stroke();
      // Suspension cables
      ctx.strokeStyle = 'rgba(197,160,89,0.35)';
      ctx.lineWidth = 1;
      for (let k = 1; k < 8; k++) {
        const cx = ax + (a.w / 8) * k;
        const cy = baseY - a.h * (1 - Math.pow(Math.abs((k / 8) - 0.5) * 2, 2)) * 0.9;
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx, baseY); ctx.stroke();
      }
      ctx.strokeStyle = '#0A1626'; ctx.lineWidth = 6;
    }
    // Dock silhouettes at water line
    ctx.fillStyle = '#050B14';
    for (const d of sky.docks) {
      const dx = wrap(d.x, 48, 0.6);
      ctx.fillRect(dx, baseY - d.h, 34, d.h);
    }
    // Warm dock lights
    ctx.fillStyle = 'rgba(255,200,120,0.7)';
    for (const d of sky.docks) {
      const dx = wrap(d.x, 48, 0.6);
      if ((dx | 0) % 2 === 0) ctx.fillRect(dx + 14, baseY - d.h + 3, 3, 3);
    }
    return;
  }

  if (sky.kind === 'neon') {
    for (const b of sky.items) {
      const bx = wrap(b.x, 90, 0.35);
      ctx.fillStyle = '#050914';
      ctx.fillRect(bx, baseY - b.h, b.w, b.h);
      const glow = `hsla(${b.hue}, 90%, 60%, 0.85)`;
      ctx.fillStyle = glow;
      ctx.fillRect(bx + 2, baseY - b.h + 4, 2, b.h - 8);
      ctx.fillRect(bx + b.w - 4, baseY - b.h + 4, 2, b.h - 8);
      ctx.shadowColor = glow; ctx.shadowBlur = 10;
      ctx.fillRect(bx + 6, baseY - b.h + 12, b.w - 12, 3);
      ctx.shadowBlur = 0;
    }
    return;
  }

  if (sky.kind === 'overpass') {
    // Concrete pillars
    ctx.fillStyle = '#0E1826';
    for (const p of sky.pillars) {
      const px = wrap(p.x, 140, 0.5);
      ctx.fillRect(px, baseY - p.h, p.w, p.h);
      // Cap
      ctx.fillStyle = '#182740';
      ctx.fillRect(px - 4, baseY - p.h, p.w + 8, 8);
      ctx.fillStyle = '#0E1826';
    }
    // Hanging exit signs
    for (const s of sky.signs) {
      const sx = wrap(s.x, 200, 0.4);
      // Support wire
      ctx.strokeStyle = 'rgba(100,120,150,0.5)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(sx + s.w / 2, 0); ctx.lineTo(sx + s.w / 2, s.y); ctx.stroke();
      // Sign body
      ctx.fillStyle = '#0F2A4A';
      ctx.fillRect(sx, s.y, s.w, s.h);
      ctx.strokeStyle = 'rgba(197,160,89,0.6)'; ctx.lineWidth = 1;
      ctx.strokeRect(sx + 0.5, s.y + 0.5, s.w - 1, s.h - 1);
      // Text
      ctx.fillStyle = 'rgba(255,255,255,0.75)';
      ctx.font = '600 9px system-ui';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('EXIT →', sx + s.w / 2, s.y + s.h / 2);
    }
    return;
  }

  if (sky.kind === 'rooftops') {
    ctx.fillStyle = '#091626';
    for (const b of sky.items) {
      const bx = wrap(b.x, 88, 0.4);
      ctx.fillRect(bx, baseY - b.h, b.w, b.h);
      // Antenna
      if (b.antenna) {
        ctx.fillStyle = '#182740';
        ctx.fillRect(bx + b.w / 2, baseY - b.h - 18, 2, 18);
        ctx.fillStyle = 'rgba(255,80,80,0.85)';
        ctx.fillRect(bx + b.w / 2 - 1, baseY - b.h - 20, 4, 4);
        ctx.fillStyle = '#091626';
      }
      // Water tank
      if (b.tank) {
        ctx.fillStyle = '#0E1E33';
        ctx.fillRect(bx + b.w * 0.3, baseY - b.h - 12, b.w * 0.4, 12);
        ctx.fillRect(bx + b.w * 0.4, baseY - b.h - 16, b.w * 0.2, 4);
        ctx.fillStyle = '#091626';
      }
      // Rooftop light strip
      ctx.fillStyle = 'rgba(197,160,89,0.5)';
      ctx.fillRect(bx + 4, baseY - b.h + 2, b.w - 8, 1);
      ctx.fillStyle = '#091626';
    }
    return;
  }

  // ridge
  ctx.fillStyle = '#0A121E';
  for (const p of sky.items) {
    const px = wrap(p.x, 160, 0.3);
    ctx.beginPath();
    ctx.moveTo(px - p.w / 2, baseY);
    ctx.lineTo(px, baseY - p.h);
    ctx.lineTo(px + p.w / 2, baseY);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = 'rgba(240,245,252,0.85)';
    ctx.beginPath();
    ctx.moveTo(px - p.w / 6, baseY - p.h * 0.72);
    ctx.lineTo(px, baseY - p.h);
    ctx.lineTo(px + p.w / 6, baseY - p.h * 0.72);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#0A121E';
  }
}

function buildMoCAmbient(weather, wind, W, H) {
  if (weather === 'snow') {
    return Array.from({ length: 60 }, () => ({ x: rand(0, W), y: rand(0, H), s: rand(1.2, 2.6), v: rand(30, 60), kind: 'snow' }));
  }
  if (weather === 'rain' || weather === 'storm') {
    const count = weather === 'storm' ? 80 : 50;
    return Array.from({ length: count }, () => ({ x: rand(0, W), y: rand(0, H), s: rand(0.8, 1.4), v: rand(500, 700), kind: 'rain' }));
  }
  if (weather === 'neon') {
    return Array.from({ length: 24 }, () => ({ x: rand(0, W), y: rand(0, H * 0.7), s: rand(1, 2), v: rand(6, 12), kind: 'neon' }));
  }
  // Wind streaks appear whenever cfg.wind > 0 (Focus/Mastery levels)
  if (wind > 0) {
    return Array.from({ length: Math.min(28, 10 + Math.round(wind * 1.2)) }, () => ({
      x: rand(0, W), y: rand(0, H * 0.85), s: rand(1, 2.2), v: rand(80, 180), phase: rand(0, Math.PI * 2), kind: 'wind',
    }));
  }
  return [];
}

function drawMoCAmbient(ctx, ambient, weather) {
  if (!ambient || ambient.length === 0) return;
  if (weather === 'snow') {
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    for (const a of ambient) { ctx.beginPath(); ctx.arc(a.x, a.y, a.s, 0, Math.PI * 2); ctx.fill(); }
  } else if (weather === 'rain' || weather === 'storm') {
    ctx.strokeStyle = weather === 'storm' ? 'rgba(180,205,235,0.6)' : 'rgba(150,190,230,0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const a of ambient) { ctx.moveTo(a.x, a.y); ctx.lineTo(a.x - 3, a.y + 12); }
    ctx.stroke();
  } else if (weather === 'neon') {
    ctx.fillStyle = 'rgba(255,80,200,0.55)';
    for (const a of ambient) { ctx.beginPath(); ctx.arc(a.x, a.y, a.s, 0, Math.PI * 2); ctx.fill(); }
  } else {
    // wind streaks
    ctx.strokeStyle = 'rgba(255,255,255,0.28)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const a of ambient) { ctx.moveTo(a.x, a.y); ctx.lineTo(a.x + 14, a.y); }
    ctx.stroke();
  }
}